/**
 * Chave de cifra do SYNC — COMPARTILHADA entre os aparelhos (ver docs/features/SYNC.md § 3, ADR 0028).
 *
 * O problema: a chave de backup é derivada da senha + um salt ALEATÓRIO POR APARELHO
 * (backupPassphrase.ts), então keyA ≠ keyB mesmo com a mesma senha — um segmento cifrado no
 * aparelho A não abre no B (tag GCM inválida). Para o sync, todos precisam da MESMA chave.
 *
 * Solução: um salt COMPARTILHADO (não é segredo — a força vem da senha via scrypt) guardado uma
 * vez na pasta do Drive (`kontto_sync_meta.json`, texto aberto). Cada aparelho deriva
 * `syncKey = scrypt(senha, saltCompartilhado)` e cacheia. Como a senha não fica salva (só a chave
 * derivada), ligar o sync PEDE a senha uma vez — exatamente o gesto do "2º aparelho" do SYNC.md.
 *
 * VERIFICADOR (`check`): o meta carrega um texto conhecido cifrado com a chave derivada. Sem ele,
 * senhas DIFERENTES entre aparelhos produziam chaves diferentes e cada lado pulava os segmentos
 * do outro em silêncio — os dois exibindo "Sincronizado ✓" para sempre sem trocar um byte. Com o
 * check, o aparelho com a senha errada descobre NA ATIVAÇÃO (erro claro), e um ciclo que topa com
 * segmento ilegível consegue diagnosticar "senha divergente" × "arquivo corrompido".
 */

import * as SecureStore from 'expo-secure-store';
import { bytesToHex, hexToBytes } from '@noble/ciphers/utils.js';
import {
    DEFAULT_KDF,
    decryptWithKey,
    deriveKey,
    encryptString,
    generateSalt,
    parseEnvelope,
    type KdfParams,
} from '@/lib/backupCrypto';
import {
    DriveApiError,
    deleteBackup,
    downloadBackup,
    listByPrefix,
    updateFileContent,
    uploadBackup,
    type DriveFile,
} from '@/lib/googleDrive/driveClient';
import { listSegments, type RemoteSegment } from './transport';

const SYNC_KEY_HEX = 'kontto_sync_key';
/**
 * Id (Drive) do meta de que a chave cacheada saiu. Redefinir a sincronização (ADR 0191) apaga o
 * meta e sobe outro, com salt novo: o id muda, e o ciclo sabe que precisa reconferir a chave sem
 * baixar o meta a cada 45s. Não é segredo — fica junto da chave só pra nascer e morrer com ela.
 */
const SYNC_META_ID = 'kontto_sync_meta_id';
export const META_NAME = 'kontto_sync_meta.json';
const KEY_CHECK_PLAINTEXT = 'kontto-sync-check-v1';

/**
 * A senha digitada não abre o verificador do espaço: a sincronização foi ligada em outro aparelho
 * com outra senha de proteção. Tipado pra tela reconhecer o caso sem comparar texto e mostrar o
 * quadro que fica no card (SPEC do card, 1.0.74); a mensagem segue a mesma (o Kontto Web a mostra).
 */
export class SyncPassphraseMismatchError extends Error {
    readonly code = 'pass_mismatch' as const;
    constructor() {
        super('Senha diferente da usada no outro aparelho. Use exatamente a mesma senha de backup em todos os aparelhos — ou, se não lembra dela, toque em "Redefinir sincronização".');
        this.name = 'SyncPassphraseMismatchError';
    }
}

interface SyncMeta {
    salt: string; // hex
    params: KdfParams;
    /** Verificador: KEY_CHECK_PLAINTEXT cifrado com a chave derivada (ausente em metas legados). */
    check?: string;
}

async function listMetas(token: string, folderId: string) {
    // listByPrefix ordena por createdTime desc → o ÚLTIMO é o mais antigo (fonte da verdade).
    return (await listByPrefix(token, folderId, META_NAME)).filter((f) => f.name === META_NAME);
}

/**
 * Lê (ou cria) o salt compartilhado na pasta do Drive. Corrida entre dois aparelhos criando ao
 * mesmo tempo: quem cria RE-LISTA em seguida e todos adotam o mais antigo — o perdedor abandona
 * o próprio meta em vez de cachear uma chave de um salt que ninguém mais usa (split-brain).
 */
async function ensureSharedMeta(token: string, folderId: string): Promise<{ meta: SyncMeta; fileId: string | null }> {
    let metas = await listMetas(token, folderId);
    if (metas.length === 0) {
        const fresh: SyncMeta = { salt: bytesToHex(generateSalt()), params: DEFAULT_KDF };
        await uploadBackup(token, folderId, META_NAME, JSON.stringify(fresh));
        metas = await listMetas(token, folderId);
        if (metas.length === 0) return { meta: fresh, fileId: null }; // não deveria acontecer
    }
    const oldest = metas[metas.length - 1];
    return { meta: JSON.parse(await downloadBackup(token, oldest.id)) as SyncMeta, fileId: oldest.id };
}

/** true se o `check` do meta abre com esta chave (senhas iguais). Check ilegível → não bloqueia. */
function keyMatchesCheck(key: Uint8Array, check: string): boolean {
    const envelope = parseEnvelope(check);
    if (!envelope) return true;
    try {
        return decryptWithKey(envelope, key) === KEY_CHECK_PLAINTEXT;
    } catch {
        return false;
    }
}

/** Quantos segmentos (o mais novo de cada aparelho) servem de prova da senha num meta legado. */
const LEGACY_PROOF_SEGMENTS = 3;

/**
 * Meta legado (sem verificador): a chave desta senha abre o que já está na pasta?
 *
 * - `'opens'`: abriu um segmento — é a chave do espaço;
 * - `'mismatch'`: há segmento legível como envelope e NENHUM abriu — senha de outro aparelho;
 * - `'empty'`: pasta sem segmento — nada a proteger, a senha daqui define o espaço;
 * - `'inconclusive'`: há segmento, mas nenhum chegou como envelope (sumiu na GC, corrompido).
 *
 * Prova pelo mais novo de cada aparelho: um segmento antigo pode ser de antes do salt
 * compartilhado e não abriria nem com a senha certa. Falha de rede propaga (não vira "vazio").
 */
async function proveKeyAgainstSpace(
    key: Uint8Array,
    token: string,
    folderId: string,
): Promise<'opens' | 'mismatch' | 'empty' | 'inconclusive'> {
    const segments = await listSegments(token, folderId);
    if (segments.length === 0) return 'empty';
    const newestPerDevice = new Map<string, RemoteSegment>();
    for (const segment of segments) newestPerDevice.set(segment.deviceId, segment); // seq crescente: o último fica
    let envelopes = 0;
    for (const segment of [...newestPerDevice.values()].slice(0, LEGACY_PROOF_SEGMENTS)) {
        let content: string;
        try {
            content = await downloadBackup(token, segment.file.id);
        } catch (error) {
            if (error instanceof DriveApiError && error.isNotFoundError) continue; // GC de outro aparelho
            throw error;
        }
        const envelope = parseEnvelope(content);
        if (!envelope) continue;
        envelopes += 1;
        try {
            decryptWithKey(envelope, key);
            return 'opens';
        } catch {
            // tag GCM inválida: este segmento não é desta chave
        }
    }
    return envelopes > 0 ? 'mismatch' : 'inconclusive';
}

/**
 * Deriva a chave de sync (senha + salt compartilhado) e cacheia no SecureStore. Roda o scrypt
 * (~4s) uma vez, ao ligar o sync. Todos os aparelhos com a MESMA senha chegam na MESMA chave.
 *
 * Se o meta tem verificador e a senha NÃO bate, LANÇA (sem cachear): melhor falhar na ativação
 * do que sincronizar "no vazio" pra sempre. Meta legado sem verificador só ganha um depois de a
 * senha abrir um segmento da pasta (ou de a pasta estar vazia): gravar o verificador de uma senha
 * errada faria o meta recusar a senha VERDADEIRA dali em diante, e como o arquivo segue com o
 * mesmo id, os aparelhos certos nem perceberiam (`verifySpaceIdentity` compara o id).
 */
export async function deriveAndCacheSyncKey(passphrase: string, token: string, folderId: string): Promise<Uint8Array> {
    const { meta, fileId } = await ensureSharedMeta(token, folderId);
    const key = await deriveKey(passphrase, hexToBytes(meta.salt), meta.params);

    if (meta.check) {
        if (!keyMatchesCheck(key, meta.check)) {
            throw new SyncPassphraseMismatchError();
        }
    } else if (fileId) {
        const proof = await proveKeyAgainstSpace(key, token, folderId);
        if (proof === 'mismatch') throw new SyncPassphraseMismatchError();
        if (proof !== 'inconclusive') await writeKeyCheck(meta, key, token, fileId);
    }

    await cacheKey(key, fileId);
    return key;
}

/** Grava o verificador num meta legado (best-effort: sem ele, o meta segue legado). */
async function writeKeyCheck(meta: SyncMeta, key: Uint8Array, token: string, fileId: string): Promise<void> {
    try {
        // SEMPRE v1: o verificador é minúsculo e um app antigo precisa abri-lo pra dizer
        // "senha divergente" em vez de "arquivo ilegível" (ADR 0181).
        const upgraded: SyncMeta = {
            ...meta,
            check: encryptString(KEY_CHECK_PLAINTEXT, key, hexToBytes(meta.salt), meta.params, 1),
        };
        await updateFileContent(token, fileId, JSON.stringify(upgraded));
    } catch {
        // best-effort: sem o upgrade, o meta segue como legado (sem verificador)
    }
}

async function cacheKey(key: Uint8Array, metaFileId: string | null): Promise<void> {
    await SecureStore.setItemAsync(SYNC_KEY_HEX, bytesToHex(key));
    if (metaFileId) await SecureStore.setItemAsync(SYNC_META_ID, metaFileId);
    else await SecureStore.deleteItemAsync(SYNC_META_ID);
}

/**
 * REDEFINE o meta do espaço (ADR 0191): apaga o(s) meta(s) que estão na pasta e sobe um novo, com
 * salt novo e verificador da senha DESTE aparelho. A partir daqui a chave antiga não abre mais
 * nada — é o que faz os outros aparelhos pararem de enviar e pedirem a senha (ver
 * `verifySpaceIdentity`). Quem chama já apagou (ou vai apagar) os segmentos: o meta sai primeiro,
 * pra que um aparelho no meio do ciclo pare de escrever o quanto antes.
 *
 * Corrida com outro aparelho criando meta ao mesmo tempo: adota o mais antigo, como o
 * `ensureSharedMeta`, e só aceita se a senha daqui abrir o verificador dele.
 */
export async function replaceSyncMeta(passphrase: string, token: string, folderId: string): Promise<Uint8Array> {
    for (const old of await listMetas(token, folderId)) await deleteBackup(token, old.id);
    const salt = generateSalt();
    const key = await deriveKey(passphrase, salt, DEFAULT_KDF);
    const fresh: SyncMeta = {
        salt: bytesToHex(salt),
        params: DEFAULT_KDF,
        check: encryptString(KEY_CHECK_PLAINTEXT, key, salt, DEFAULT_KDF, 1),
    };
    await uploadBackup(token, folderId, META_NAME, JSON.stringify(fresh));
    const metas = await listMetas(token, folderId);
    const oldest = metas[metas.length - 1];
    if (!oldest) throw new Error('O Google Drive não guardou a configuração nova da sincronização. Tente de novo.');
    const adopted = JSON.parse(await downloadBackup(token, oldest.id)) as SyncMeta;
    const adoptedKey = adopted.salt === fresh.salt ? key : await deriveKey(passphrase, hexToBytes(adopted.salt), adopted.params);
    if (adopted.check && !keyMatchesCheck(adoptedKey, adopted.check)) {
        throw new Error('Outro aparelho redefiniu a sincronização ao mesmo tempo, com outra senha. Tente de novo.');
    }
    await cacheKey(adoptedKey, oldest.id);
    return adoptedKey;
}

/**
 * A chave CACHEADA ainda é a do espaço que está na pasta? Roda no começo de TODO ciclo, antes de
 * qualquer upload (ADR 0191): um aparelho com a chave de antes de uma redefinição que seguisse
 * subindo segmentos deixaria na pasta arquivos que ninguém mais abre — e segmento ilegível
 * BLOQUEIA o aparelho que o lê (ADR 0039).
 *
 * `metaFiles` vem da listagem que o ciclo já fez (nada de ida extra ao Drive). O meta só é
 * baixado quando o id mudou (ou nunca foi guardado — aparelho de antes desta versão).
 *
 * - `'ok'`: mesmo meta, ou um meta que esta chave abre (o id novo é guardado);
 * - `'reset'`: o meta que a chave conhecia sumiu, ou o que está lá não abre com ela.
 *   Meta ausente sem id guardado é inconclusivo e passa (`'ok'`): é o espaço que o próprio
 *   ciclo ainda vai criar, não uma redefinição.
 */
export async function verifySpaceIdentity(token: string, metaFiles: DriveFile[]): Promise<'ok' | 'reset'> {
    const key = await getCachedSyncKey();
    if (!key) return 'ok';
    const stored = await SecureStore.getItemAsync(SYNC_META_ID);
    const metas = metaFiles
        .filter((f) => f.name === META_NAME)
        .sort((a, b) => ((a.createdTime ?? '') < (b.createdTime ?? '') ? 1 : -1));
    const oldest = metas[metas.length - 1];
    if (!oldest) return stored ? 'reset' : 'ok';
    if (stored === oldest.id) return 'ok';
    const meta = JSON.parse(await downloadBackup(token, oldest.id)) as SyncMeta;
    if (meta.check && !keyMatchesCheck(key, meta.check)) return 'reset';
    await SecureStore.setItemAsync(SYNC_META_ID, oldest.id);
    return 'ok';
}

/**
 * Confere a chave CACHEADA contra o verificador do meta no Drive. Usado quando um ciclo topa com
 * segmento ilegível: distingue "senha divergente entre aparelhos" (false) de "arquivo corrompido/
 * peer mais novo" (true). null = sem chave cacheada ou meta sem verificador (inconclusivo).
 */
export async function cachedKeyMatchesMeta(token: string, folderId: string): Promise<boolean | null> {
    const key = await getCachedSyncKey();
    if (!key) return null;
    const metas = await listMetas(token, folderId);
    if (metas.length === 0) return null;
    const meta = JSON.parse(await downloadBackup(token, metas[metas.length - 1].id)) as SyncMeta;
    if (!meta.check) return null;
    return keyMatchesCheck(key, meta.check);
}

/** Id do meta de que a chave cacheada saiu (null: sem chave, ou aparelho de antes do ADR 0191). */
export async function getCachedSyncMetaId(): Promise<string | null> {
    return SecureStore.getItemAsync(SYNC_META_ID);
}

/** Chave de sync cacheada, ou null se o sync ainda não foi configurado neste aparelho. */
export async function getCachedSyncKey(): Promise<Uint8Array | null> {
    const hex = await SecureStore.getItemAsync(SYNC_KEY_HEX);
    return hex ? hexToBytes(hex) : null;
}

export async function clearSyncKey(): Promise<void> {
    await SecureStore.deleteItemAsync(SYNC_KEY_HEX);
    await SecureStore.deleteItemAsync(SYNC_META_ID);
}
